import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { FaCoins, FaHistory, FaInfoCircle, FaTrophy, FaRedo, FaVolumeUp, FaVolumeMute } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import "./TeenPattiGame.css";

const API_URL = process.env.REACT_APP_API_URL || "";
const BETS = [10, 20, 50, 100, 200, 500, 1000, 5000];
const PLAYERS = { "2-player": 2, "3-player": 3, "4-player": 4 };

const money = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const suitSymbol = { spades: "♠", hearts: "♥", diamonds: "♦", clubs: "♣" };
const suitClass = { spades: "black", hearts: "red", diamonds: "red", clubs: "black" };
const rankLabel = { 11: "J", 12: "Q", 13: "K", 14: "A" };

const labelRank = (r) => rankLabel[r] || r;

// Lightweight game sounds generated with Web Audio API. No external audio files required.
function createGameSound() {
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) return null;
  const ctx = new AudioCtx();

  const tone = (frequency, duration = 0.08, type = "sine", volume = 0.035, delay = 0) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(frequency, ctx.currentTime + delay);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime + delay);
    gain.gain.exponentialRampToValueAtTime(volume, ctx.currentTime + delay + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + delay + duration);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(ctx.currentTime + delay);
    osc.stop(ctx.currentTime + delay + duration + 0.015);
  };

  return {
    unlock: async () => { if (ctx.state === "suspended") await ctx.resume(); },
    click: () => { tone(520, 0.055, "sine", 0.025); },
    deal: () => { tone(180, 0.07, "triangle", 0.04); tone(430, 0.045, "sine", 0.018, 0.035); },
    showdown: () => { tone(260, 0.12, "triangle", 0.04); tone(520, 0.18, "triangle", 0.04, 0.09); tone(780, 0.24, "sine", 0.035, 0.19); },
    win: () => { [523, 659, 784, 1047].forEach((n, i) => tone(n, 0.18, "sine", 0.045, i * 0.09)); },
    lose: () => { tone(330, 0.18, "sawtooth", 0.025); tone(220, 0.28, "sawtooth", 0.022, 0.13); },
  };
}

function PlayingCard({ card, hidden = false, small = false }) {
  if (hidden) return <div className={`tp-card tp-back ${small ? "small" : ""}`}><span>♠</span><b>3P</b><span>♥</span></div>;
  return (
    <div className={`tp-card ${suitClass[card.suit]} ${small ? "small" : ""}`}>
      <div className="tp-card-rank">{labelRank(card.rank)}</div>
      <div className="tp-card-suit">{suitSymbol[card.suit]}</div>
      <div className="tp-card-center">{suitSymbol[card.suit]}</div>
    </div>
  );
}

export default function TeenPattiGame() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const api = useMemo(() => ({ headers: { Authorization: token } }), [token]);

  const [balance, setBalance] = useState(0);
  const [mode, setMode] = useState("2-player");
  const [betAmount, setBetAmount] = useState(100);
  const [gameCode, setGameCode] = useState(null);
  const [players, setPlayers] = useState([]);
  const [phase, setPhase] = useState("setup"); // setup | dealing | table | result
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [busy, setBusy] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => localStorage.getItem("teenpattiSound") !== "off");
  const soundRef = useRef(null);

  const playSound = useCallback(async (name) => {
    if (!soundEnabled) return;
    try {
      if (!soundRef.current) soundRef.current = createGameSound();
      if (!soundRef.current) return;
      await soundRef.current.unlock();
      soundRef.current[name]?.();
    } catch (e) {
      // Audio is optional; never let sound errors affect game settlement.
    }
  }, [soundEnabled]);

  const toggleSound = () => {
    setSoundEnabled((current) => {
      const nextValue = !current;
      localStorage.setItem("teenpattiSound", nextValue ? "on" : "off");
      if (nextValue) window.setTimeout(() => playSound("click"), 0);
      return nextValue;
    });
  };

  const human = players.find((p) => p.isUser);
  const opponents = players.filter((p) => !p.isUser);
  const prizeGross = Number(betAmount) * PLAYERS[mode];
  const prizeNet = prizeGross * 0.9;

  const load = async () => {
    try {
      const [activeRes, historyRes] = await Promise.all([
        axios.get(`${API_URL}/api/teenpatti/active`, api),
        axios.get(`${API_URL}/api/teenpatti/history`, api),
      ]);
      if (activeRes.data?.success) {
        setBalance(Number(activeRes.data.balance || 0));
        if (activeRes.data.active && activeRes.data.data) {
          const g = activeRes.data.data;
          setGameCode(g.gameCode);
          setMode(g.mode);
          setBetAmount(Number(g.betAmount));
          setPlayers(g.players || []);
          setPhase("table");
        }
      }
      if (historyRes.data?.success) setHistory(historyRes.data.data || []);
    } catch (e) {
      if (e.response?.status === 401) toast.error("Please login again");
    }
  };

  useEffect(() => {
    if (!token) { navigate("/login"); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = async () => {
    if (busy) return;
    const amount = Number(betAmount);
    if (!Number.isFinite(amount) || amount <= 0) return toast.error("Enter a valid bet amount");
    if (amount > balance) return toast.error("Insufficient balance");
    setBusy(true);
    await playSound("click");
    try {
      const res = await axios.post(`${API_URL}/api/teenpatti/start`, { mode, betAmount: amount }, api);
      if (!res.data?.success) return toast.error(res.data?.message || "Unable to start game");
      setBalance(Number(res.data.balance || 0));
      setGameCode(res.data.gameCode);
      setPlayers(res.data.players || []);
      setResult(null);
      setPhase("dealing");
      for (let i = 0; i < PLAYERS[mode] * 3; i += 1) {
        window.setTimeout(() => playSound("deal"), 180 + i * 190);
      }
      window.setTimeout(() => setPhase("table"), 2600);
    } catch (e) {
      toast.error(e.response?.data?.message || "Unable to start game");
    } finally { setBusy(false); }
  };

  const showdown = async () => {
    if (!gameCode || busy || phase !== "table") return;
    setBusy(true);
    await playSound("showdown");
    try {
      const res = await axios.post(`${API_URL}/api/teenpatti/finish`, { gameCode }, api);
      if (!res.data?.success) return toast.error(res.data?.message || "Unable to settle game");
      setPlayers(res.data.players || players);
      setResult(res.data);
      setPhase("result");
      window.setTimeout(() => playSound(res.data.result === "win" ? "win" : "lose"), 520);
      setBalance(Number(res.data.balance || 0));
      const h = await axios.get(`${API_URL}/api/teenpatti/history`, api);
      if (h.data?.success) setHistory(h.data.data || []);
    } catch (e) {
      toast.error(e.response?.data?.message || "Unable to settle game");
    } finally { setBusy(false); }
  };

  const next = () => {
    playSound("click");
    setGameCode(null); setPlayers([]); setResult(null); setPhase("setup");
  };

  return (
    <div className="tp-page">
      <header className="tp-topbar">
        <button className="tp-back" onClick={() => navigate("/")}>←</button>
        <div className="tp-brand">
          <div className="tp-brand-mark">♠</div>
          <div><strong>TEEN PATTI</strong><small>Royal table • 3 cards</small></div>
        </div>
        <button className="tp-sound" onClick={toggleSound} aria-label={soundEnabled ? "Mute game sounds" : "Enable game sounds"}>{soundEnabled ? <FaVolumeUp /> : <FaVolumeMute />}</button>
        <div className="tp-wallet"><FaCoins /><div><small>Balance</small><b>{money(balance)}</b></div></div>
      </header>

      <main className="tp-wrap">
        <section className="tp-title-row">
          <div><span className="tp-live"><i /> TABLE OPEN</span><h1>Royal <span>Teen Patti</span></h1><p>Choose the table size, place your stake, receive three cards and reveal the showdown.</p></div>
          <div className="tp-table-stats"><div><b>2–4</b><small>Players</small></div><div><b>3</b><small>Cards each</small></div><div><b>2×+</b><small>Prize pool</small></div></div>
        </section>

        <section className="tp-layout">
          <div className="tp-table-card">
            <div className="tp-table-head">
              <div><span>TABLE CONFIGURATION</span><h2>{PLAYERS[mode]} PLAYER TABLE</h2></div>
              <button className="tp-info" onClick={() => setShowRules(true)}><FaInfoCircle /></button>
            </div>

            {phase === "setup" && (
              <div className="tp-setup">
                <div className="tp-mode-row">
                  {Object.entries(PLAYERS).map(([key, count]) => (
                    <button key={key} className={mode === key ? "selected" : ""} onClick={() => setMode(key)} disabled={busy}>
                      <strong>{count}</strong><span>PLAYERS</span><small>{count === 2 ? "Heads-up" : count === 3 ? "Classic" : "Full table"}</small>
                    </button>
                  ))}
                </div>
                <div className="tp-deck-preview"><div className="tp-preview-card p1">A♠</div><div className="tp-preview-card p2">K♥</div><div className="tp-preview-card p3">Q♦</div><div><b>DEAL 3 CARDS</b><span>One fair shuffled deck per round</span></div></div>
                <label className="tp-label">STAKE AMOUNT</label>
                <div className="tp-bets">{BETS.map((v) => <button key={v} className={Number(betAmount) === v ? "active" : ""} onClick={() => setBetAmount(v)} disabled={busy}>₹{v.toLocaleString("en-IN")}</button>)}</div>
                <div className="tp-custom"><span>₹</span><input type="number" min="1" value={betAmount} onChange={(e) => setBetAmount(e.target.value)} /><small>{money(balance)} available</small></div>
                <div className="tp-payout"><div><small>YOUR STAKE</small><b>{money(betAmount)}</b></div><div><small>WINNER NET</small><b>{money(prizeNet)}</b></div></div>
                <button className="tp-primary" onClick={start} disabled={busy}>{busy ? "STARTING TABLE…" : "♠  DEAL & PLAY"}</button>
              </div>
            )}

            {(phase === "dealing" || phase === "table" || phase === "result") && (
              <div className="tp-felt">
                <div className="tp-table-rim"><div className="tp-table-inner">
                  <div className="tp-center-badge"><span>ROYAL</span><b>♠</b><span>TEEN PATTI</span></div>
                  {opponents.map((p, i) => (
                    <div className={`tp-seat tp-opponent seat-${i + 1} ${phase === "dealing" ? "tp-dealing-seat" : ""}`} key={p.id}>
                      <div className="tp-avatar">{p.avatar ? <img src={p.avatar} alt="" /> : "♟"}</div>
                      <div className="tp-seat-name">{p.name}</div>
                      <div className="tp-opponent-cards">{(p.cards || [1,2,3]).map((c, j) => <div key={j} className="tp-distributed-card" style={{ "--deal-index": i * 3 + j }}><PlayingCard card={c} hidden={phase !== "result"} small /></div>)}</div>
                      {phase === "result" && <span className={`tp-rank-tag ${p.isWinner ? "winner" : ""}`}>{p.handName || "SHOW"}</span>}
                    </div>
                  ))}
                  <div className="tp-seat tp-user-seat">
                    <div className="tp-player-glow" />
                    <div className="tp-avatar user">YOU</div>
                    <div className="tp-seat-name">YOU</div>
                    <div className={`tp-user-cards ${phase === "dealing" ? "tp-user-dealing" : ""}`}>{(human?.cards || []).map((c, j) => <div className={`tp-deal-card d${j}`} style={{ "--deal-index": opponents.length * 3 + j }} key={j}><PlayingCard card={c} /></div>)}</div>
                    {human?.handName && <span className={`tp-rank-tag ${human.isWinner ? "winner" : ""}`}>{human.handName}</span>}
                  </div>
                </div></div>
                <div className="tp-round-code">ROUND <b>{gameCode}</b></div>
                {phase === "dealing" && <div className="tp-dealing"><span>DEALING CARDS</span><em>• ONE BY ONE</em><div className="tp-dots"><i/><i/><i/></div></div>}
                {phase === "table" && <button className="tp-showdown" onClick={showdown} disabled={busy}>SHOWDOWN <span>♠</span></button>}
                {phase === "result" && <div className={`tp-result-banner ${result?.result === "win" ? "win" : "lose"}`}><div><small>SHOWDOWN</small><strong>{result?.result === "win" ? "YOU WIN" : "YOU LOSE"}</strong><span>{result?.winnerName} takes the table</span></div><b>{result?.result === "win" ? `+${money(result.winAmount)}` : `-${money(result.betAmount)}`}</b><button onClick={next}><FaRedo /> NEXT</button></div>}
              </div>
            )}
          </div>

          <aside className="tp-side">
            <div className="tp-side-head"><div><FaHistory /><b>RECENT TABLES</b></div><button onClick={() => setShowHistory(true)}>VIEW ALL</button></div>
            <div className="tp-history">
              {history.slice(0, 8).map((h) => <div className="tp-history-row" key={h.id || h.game_code}><div className={`tp-history-mark ${h.result === "win" ? "win" : "lose"}`}>{h.result === "win" ? "W" : "L"}</div><div><b>{h.mode}</b><small>{h.game_code}</small></div><strong className={h.result === "win" ? "positive" : "negative"}>{h.result === "win" ? `+${money(h.win_amount)}` : `-${money(h.bet_amount)}`}</strong></div>)}
              {!history.length && <div className="tp-empty">No tables played yet.</div>}
            </div>
            <div className="tp-fair"><FaTrophy /><div><b>FAIR SHUFFLE</b><span>Cards are shuffled and dealt by the server. The UI never decides the winner.</span></div></div>
            <div className="tp-rules-mini"><b>HAND ORDER</b><span>Trail → Pure Sequence → Sequence → Color → Pair → High Card</span></div>
          </aside>
        </section>
      </main>

      {showRules && <div className="tp-modal-bg" onClick={() => setShowRules(false)}><div className="tp-modal" onClick={(e) => e.stopPropagation()}><button onClick={() => setShowRules(false)}>×</button><div className="tp-modal-icon">♠</div><h2>Teen Patti rules</h2><ol><li>Select a 2, 3 or 4 player table.</li><li>Choose your stake and deal the round.</li><li>Every player receives exactly three cards from one shuffled 52-card deck.</li><li>Reveal the showdown. The strongest hand wins the table.</li></ol><div className="tp-order"><b>Trail</b><b>Pure Sequence</b><b>Sequence</b><b>Color</b><b>Pair</b><b>High Card</b></div></div></div>}
      {showHistory && <div className="tp-modal-bg" onClick={() => setShowHistory(false)}><div className="tp-modal tp-history-modal" onClick={(e) => e.stopPropagation()}><button onClick={() => setShowHistory(false)}>×</button><h2>Teen Patti History</h2><div className="tp-history-full">{history.map((h) => <div className="tp-full-row" key={h.id || h.game_code}><span className={`tp-history-mark ${h.result === "win" ? "win" : "lose"}`}>{h.result === "win" ? "W" : "L"}</span><div><b>{h.mode}</b><small>{h.game_code}</small></div><div><small>BET</small><b>{money(h.bet_amount)}</b></div><div className={h.result === "win" ? "positive" : "negative"}><small>{h.result?.toUpperCase()}</small><b>{h.result === "win" ? `+${money(h.win_amount)}` : `-${money(h.bet_amount)}`}</b></div></div>)}</div></div></div>}
    </div>
  );
}
